import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Divider,
  useTheme,
  alpha,
} from '@mui/material';
import {
  PersonAdd as SignUpIcon,
  Login as LoginIcon,
  Business as BusinessIcon,
} from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { createUserWithEmailAndPassword, updateProfile } from 'firebase/auth';
import { auth } from '../config/firebase';
import { apiService } from '../services/api';

const SignUpForm: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [isCustomer, setIsCustomer] = useState(false);
  const [customerName, setCustomerName] = useState('');
  const [customerEmail, setCustomerEmail] = useState('');
  const [customerPhone, setCustomerPhone] = useState('');
  const theme = useTheme();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    // Validation
    if (!email.trim() || !password.trim() || !confirmPassword.trim() || !displayName.trim() || !customerName.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    setError('');
    setSuccess('');

    try {
      // Create Firebase user
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // Update user profile
      await updateProfile(user, {
        displayName: displayName
      });

      // Create customer record in database via API
      try {
        const customerData = await apiService.autoCreateCustomerForFirebaseUser({
          firebase_uid: user.uid,
          email: email,
          display_name: customerName || displayName
        });
        
        console.log('✅ Customer record created:', customerData);
        
        // Store additional customer info locally if needed
        const localCustomerData = {
          name: customerName,
          email: email,
          phone: customerPhone,
          userId: user.uid,
          customerId: customerData.customer_id,
          apiKey: customerData.api_key,
          createdAt: new Date().toISOString()
        };
        localStorage.setItem('customerData', JSON.stringify(localCustomerData));
        
        setSuccess(`Account created successfully! Customer ID: ${customerData.customer_id}. You can now log in.`);
        
      } catch (apiError: any) {
        console.error('Failed to create customer record:', apiError);
        
        // Fallback: Store customer data locally if API fails
        const fallbackCustomerData = {
          name: customerName,
          email: email,
          phone: customerPhone,
          userId: user.uid,
          createdAt: new Date().toISOString(),
          needsSync: true // Flag to indicate this needs to be synced to database
        };
        localStorage.setItem('customerData', JSON.stringify(fallbackCustomerData));
        
        setSuccess('Account created successfully! Customer data will be synced when you log in. You can now log in.');
      }
      
      // Clear form
      setEmail('');
      setPassword('');
      setConfirmPassword('');
      setDisplayName('');
      setCustomerName('');
      setCustomerEmail('');
      setCustomerPhone('');
      
    } catch (error: any) {
      console.error('Sign up error:', error);
      if (error.code === 'auth/email-already-in-use') {
        setError('An account with this email already exists');
      } else if (error.code === 'auth/weak-password') {
        setError('Password is too weak');
      } else {
        setError('Failed to create account. Please try again.');
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      className="jb-auth-root"
      sx={{
        display: 'flex',
        alignItems: 'stretch',
        justifyContent: 'center',
      }}
    >
      {/* Background layers (matching landing page hero) */}
      <div className="jb-auth-layer jb-auth-grid" />
      <div className="jb-auth-layer jb-auth-conic" />
      <div className="jb-auth-blob jb-auth-blob--1" />
      <div className="jb-auth-blob jb-auth-blob--2" />
      <div className="jb-auth-blob jb-auth-blob--3" />

      <Box
        sx={{
          position: 'relative',
          zIndex: 1,
          display: 'flex',
          width: '100%',
          height: '100vh',
          flexDirection: { xs: 'column', md: 'row' },
        }}
      >
        {/* Left Panel - Branding with Theme Color */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            p: { xs: 3, md: 6 },
            background: `linear-gradient(135deg,
              ${alpha(theme.palette.primary.main, 0.92)} 0%,
              ${alpha(theme.palette.secondary.main, 0.72)} 55%,
              ${alpha(theme.palette.warning.main, 0.42)} 100%)`,
            color: 'white',
            textAlign: 'center',
            minHeight: { xs: '40vh', md: '100vh' },
            position: 'relative',
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              inset: 0,
              backgroundImage:
                'linear-gradient(to right, rgba(255,255,255,0.14) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.14) 1px, transparent 1px)',
              backgroundSize: '26px 26px',
              opacity: 0.35,
              pointerEvents: 'none',
            }}
          />

          <Box
            sx={{
              width: 120,
              height: 120,
              borderRadius: '50%',
              background: alpha(theme.palette.common.white, 0.2),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              mb: 3,
              backdropFilter: 'blur(10px)',
              border: `1px solid ${alpha(theme.palette.common.white, 0.3)}`,
              position: 'relative',
              zIndex: 1,
            }}
          >
            <BusinessIcon
              sx={{
                fontSize: '3rem',
                color: 'white',
                filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.2))',
              }}
            />
          </Box>

          <Typography
            variant="h2"
            sx={{
              fontWeight: 700,
              mb: 2,
              textShadow: '0 2px 4px rgba(0, 0, 0, 0.2)',
              letterSpacing: '-0.02em',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Join JackBot
          </Typography>

          <Typography
            variant="h6"
            sx={{
              opacity: 0.9,
              lineHeight: 1.6,
              mb: 4,
              fontWeight: 400,
              maxWidth: '400px',
              color: 'white',
              position: 'relative',
              zIndex: 1,
            }}
          >
            Create your account and start managing your AI chatbot integrations today
          </Typography>

          <Box sx={{ textAlign: 'left', maxWidth: '300px', color: 'white', position: 'relative', zIndex: 1 }}>
            {[
              'Easy Setup Process',
              'Instant API Access',
              'Full Admin Dashboard',
              'Customer Support'
            ].map((feature, index) => (
              <Box
                key={index}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  mb: 1.5,
                  opacity: 0.9,
                  color: 'white',
                }}
              >
                <Box
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    bgcolor: alpha(theme.palette.common.white, 0.8),
                    mr: 2,
                  }}
                />
                <Typography variant="body1">{feature}</Typography>
              </Box>
            ))}
          </Box>
        </Box>

        {/* Right Panel - Sign Up Form with White Background */}
        <Box
          sx={{
            flex: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            p: { xs: 3, md: 4 },
            bgcolor: 'transparent',
            overflow: 'auto',
            minHeight: { xs: '60vh', md: '100vh' },
          }}
        >
          <Paper
            elevation={0}
            sx={{
              width: '100%',
              maxWidth: 520,
              px: { xs: 3, md: 4 },
              py: { xs: 3, md: 4 },
              my: { xs: 2, md: 0 },
              borderRadius: 4,
              backgroundColor: alpha(theme.palette.common.white, 0.78),
              border: `1px solid ${alpha(theme.palette.common.white, 0.45)}`,
              backdropFilter: 'blur(16px)',
              boxShadow: '0 20px 60px rgba(17, 24, 39, 0.12)',
            }}
          >
            <Box sx={{ textAlign: 'center', mb: 4 }}>
              <Typography
                variant="h4"
                sx={{
                  fontWeight: 600,
                  color: 'text.primary',
                  mb: 1,
                }}
              >
                Create Account
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Get started with your JackBot admin account
              </Typography>
            </Box>

            {error && (
              <Alert severity="error" sx={{ mb: 3, borderRadius: 2 }}>
                {error}
              </Alert>
            )}

            {success && (
              <Alert severity="success" sx={{ mb: 3, borderRadius: 2 }}>
                {success}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit} noValidate>
              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  required
                  disabled={loading}
                  autoFocus
                />
                <TextField
                  fullWidth
                  label="Customer Name"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  required
                  disabled={loading}
                />
              </Box>

              <TextField
                fullWidth
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                margin="normal"
                required
                disabled={loading}
                autoComplete="email"
                sx={{ mb: 2 }}
              />

              <TextField
                fullWidth
                label="Phone (Optional)"
                value={customerPhone}
                onChange={(e) => setCustomerPhone(e.target.value)}
                margin="normal"
                disabled={loading}
                sx={{ mb: 2 }}
              />

              <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
                <TextField
                  fullWidth
                  label="Password"
                  type="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
                <TextField
                  fullWidth
                  label="Confirm Password"
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  disabled={loading}
                  autoComplete="new-password"
                />
              </Box>

              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading}
                startIcon={loading ? <CircularProgress size={20} /> : <SignUpIcon />}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 600,
                  mb: 3,
                }}
              >
                {loading ? 'Creating Account...' : 'Create Account'}
              </Button>

              <Divider sx={{ my: 3 }}>
                <Typography variant="body2" color="text.secondary">
                  Already have an account?
                </Typography>
              </Divider>

              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={<LoginIcon />}
                onClick={() => navigate('/login')}
                disabled={loading}
                sx={{
                  py: 1.5,
                  fontSize: '1rem',
                  fontWeight: 500,
                }}
              >
                Sign In Instead
              </Button>
            </Box>
          </Paper>
        </Box>
      </Box>
    </Box>
  );
};

export default SignUpForm; 